import { Box, styled } from '@mui/material';
import { type ForwardedRef, forwardRef, useContext } from 'react';
import { useSwimlaneEventPublisher } from '../../api';
import { SwimlaneStateContext, useExportMode, useSwimlaneRootProps } from '../../context';
import { getCellWidth } from '../body/cards/CardUtils';
import {
  AXIS_HEIGHT,
  CELL_GAP,
  COLUMN_HEADER_HEIGHT,
  GRID_GAP,
  ROW_HEADER_WIDTH,
  ROW_HEIGHT_MIN,
} from '../constants';

const baseContainer = {
  lineHeight: AXIS_HEIGHT.label,
  zIndex: 10,
  boxSizing: 'content-box',
} as const;

const ColumnAxisContainer = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'isHeadersSticky' &&
    prop !== 'backgroundColor' &&
    prop !== 'isPngPublicationTemplate' &&
    prop !== 'isPdfExportTemplate',
})<{
  isHeadersSticky: boolean;
  backgroundColor: string;
  isPngPublicationTemplate?: boolean;
  isPdfExportTemplate?: boolean;
}>(({ isHeadersSticky, backgroundColor, isPngPublicationTemplate, isPdfExportTemplate }) => ({
  ...baseContainer,
  position:
    isHeadersSticky && !isPngPublicationTemplate && !isPdfExportTemplate ? 'sticky' : 'relative',
  cursor: 'pointer',
  gridArea: 'axisX',
  height: AXIS_HEIGHT.label,
  top: '0px',
  borderBottom: `${GRID_GAP.label} solid ${backgroundColor}`,
  backgroundColor,
}));

const RowAxisContainer = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'isHeadersSticky' &&
    prop !== 'backgroundColor' &&
    prop !== 'isPngPublicationTemplate' &&
    prop !== 'isPdfExportTemplate',
})<{
  isHeadersSticky: boolean;
  backgroundColor: string;
  isPngPublicationTemplate?: boolean;
  isPdfExportTemplate?: boolean;
}>(({ isHeadersSticky, backgroundColor, isPngPublicationTemplate, isPdfExportTemplate }) => ({
  ...baseContainer,
  position:
    isHeadersSticky && !isPngPublicationTemplate && !isPdfExportTemplate ? 'sticky' : 'relative',
  gridArea: 'axisY',
  cursor: 'pointer',
  width: AXIS_HEIGHT.label,
  left: '0px',
  // Opaque canvas fill so the sticky axis masks cards scrolling underneath it;
  // the pill on top uses a translucent neutral tint.
  backgroundColor,
  borderRight: `${GRID_GAP.label} solid ${backgroundColor}`,
}));

const baseAxisStyles = {
  display: 'flex',
  // Neutral tint that stays distinct from the canvas in both light and dark
  // themes (faint gray on light, faint light on dark) - it flips with `.dark`.
  backgroundColor: 'var(--color-bg-neutral-subtle)',
  color: 'var(--color-text-neutral-primary)',
  borderRadius: '8px',
} as const;

const ColumnAxis = styled('span')(baseAxisStyles);

const RowAxis = styled('span', {
  shouldForwardProp: (prop) => prop !== 'isPngPublicationTemplate',
})<{ isPngPublicationTemplate?: boolean }>(({ isPngPublicationTemplate }) => ({
  ...baseAxisStyles,
  // Override baseAxis display: flex to allow rotated text rendering in export mode
  display: isPngPublicationTemplate ? 'block' : baseAxisStyles.display,
  height: '100%',
}));

const baseLabel = {
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textAlign: 'center',
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  fontWeight: 600,
  letterSpacing: '0.025em',
} as const;

const ColumnAxisLabel = styled('div', {
  shouldForwardProp: (prop) =>
    prop !== 'isHeadersSticky' &&
    prop !== 'isPngPublicationTemplate' &&
    prop !== 'isPdfExportTemplate',
})<{ isHeadersSticky: boolean; isPngPublicationTemplate?: boolean; isPdfExportTemplate?: boolean }>(
  ({ isHeadersSticky, isPngPublicationTemplate, isPdfExportTemplate }) => ({
    ...baseLabel,
    position:
      isHeadersSticky && !isPngPublicationTemplate && !isPdfExportTemplate ? 'sticky' : 'relative',
  }),
);

const RowAxisLabel = styled('div', {
  shouldForwardProp: (prop) =>
    prop !== 'isHeadersSticky' &&
    prop !== 'isPngPublicationTemplate' &&
    prop !== 'isPdfExportTemplate',
})<{ isHeadersSticky: boolean; isPngPublicationTemplate?: boolean; isPdfExportTemplate?: boolean }>(
  ({ isHeadersSticky, isPngPublicationTemplate, isPdfExportTemplate }) => ({
    writingMode: 'vertical-lr',
    transform: 'rotate(180deg)',
    position:
      isHeadersSticky && !isPngPublicationTemplate && !isPdfExportTemplate ? 'sticky' : 'relative',
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    fontWeight: 600,
    letterSpacing: '0.025em',
    textAlign: 'center',
    height: '100%',
    ...(isPngPublicationTemplate
      ? {
          // PNG export: allow overflow for rotated text to prevent cutoff
          overflow: 'visible',
        }
      : {
          // Interactive UI: constrain overflow with ellipsis
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }),
  }),
);

export const SwimlaneAxisLabelTop = forwardRef((_props, ref: ForwardedRef<HTMLDivElement>) => {
  const {
    columnAxisLabelText,
    rowAxisLabelText,
    rowHeaderOrientation,
    rowHeadersWidth,
    isHeadersSticky,
    columnHeaders,
    isCompact,
    backgroundColor,
  } = useSwimlaneRootProps();
  const { isPng: isPngPublicationTemplate, isPdf: isPdfExportTemplate } = useExportMode();
  const publishEvent = useSwimlaneEventPublisher();

  const columnAxisLabelWidth = getColumnAxisLabelWidth(columnHeaders.length, isCompact);

  const rowHeaderOffset =
    rowHeaderOrientation === 'horizontal' ? rowHeadersWidth : ROW_HEADER_WIDTH.vertical.value;
  const widthOffset =
    typeof rowAxisLabelText === 'string'
      ? rowHeaderOffset + GRID_GAP.value + AXIS_HEIGHT.value
      : rowHeaderOffset;

  if (columnAxisLabelText) {
    return (
      <ColumnAxisContainer
        className="fs-exclude"
        isHeadersSticky={!!isHeadersSticky}
        isPngPublicationTemplate={isPngPublicationTemplate}
        isPdfExportTemplate={isPdfExportTemplate}
        backgroundColor={backgroundColor}
        role="columnheader"
        data-export="column-header-container"
        data-testid="main-columnheader"
        sx={{ width: `${columnAxisLabelWidth}px` }}
      >
        <ColumnAxis
          style={!isPngPublicationTemplate ? {} : { justifyContent: 'center' }}
          data-testid="column-axis"
        >
          <ColumnAxisLabel
            ref={ref}
            isHeadersSticky={!!isHeadersSticky}
            isPngPublicationTemplate={isPngPublicationTemplate}
            isPdfExportTemplate={isPdfExportTemplate}
            style={
              isPdfExportTemplate
                ? { width: '100%' }
                : !isPngPublicationTemplate
                  ? {
                      width: `calc(100vw - ${widthOffset}px)`,
                      left: `${widthOffset}px`,
                    }
                  : {
                      alignSelf: 'center',
                    }
            }
            onClick={(e) => {
              publishEvent('axisLabelClicked', { axis: 'column', anchorEl: e.currentTarget });
            }}
          >
            {columnAxisLabelText}
          </ColumnAxisLabel>
        </ColumnAxis>
      </ColumnAxisContainer>
    );
  }
  return null;
});

export const SwimlaneAxisLabelLeft = forwardRef((_props, ref: ForwardedRef<HTMLDivElement>) => {
  const { rowAxisLabelText, columnAxisLabelText, isHeadersSticky, rowHeaders, backgroundColor } =
    useSwimlaneRootProps();
  const { isPng: isPngPublicationTemplate, isPdf: isPdfExportTemplate } = useExportMode();
  const swimlaneStateContext = useContext(SwimlaneStateContext);
  const publishEvent = useSwimlaneEventPublisher();

  if (!rowAxisLabelText) {
    return null;
  }

  const heightOffset =
    typeof columnAxisLabelText === 'string'
      ? AXIS_HEIGHT.value + GRID_GAP.value + COLUMN_HEADER_HEIGHT.value
      : COLUMN_HEADER_HEIGHT.value;
  const nonGroupRowHeaders = rowHeaders.filter(
    (h) => !('isGroupHeader' in h && h.isGroupHeader === true),
  );
  const rowAxisHeight = swimlaneStateContext?.getRowHeight
    ? getRowAxisLabelWidth(
        nonGroupRowHeaders.map((header) => header.id),
        swimlaneStateContext.getRowHeight,
      )
    : 100;

  return (
    <RowAxisContainer
      className="fs-exclude"
      isHeadersSticky={!!isHeadersSticky}
      isPngPublicationTemplate={isPngPublicationTemplate}
      isPdfExportTemplate={isPdfExportTemplate}
      backgroundColor={backgroundColor}
      role="rowheader"
      data-export="row-header"
      data-roadmap-y-axis-label="true"
      data-testid="main-rowheader"
      sx={{
        height: `${rowAxisHeight}px`,
      }}
    >
      <RowAxis data-testid="row-axis" isPngPublicationTemplate={isPngPublicationTemplate}>
        <RowAxisLabel
          ref={ref}
          isHeadersSticky={!!isHeadersSticky}
          isPngPublicationTemplate={isPngPublicationTemplate}
          isPdfExportTemplate={isPdfExportTemplate}
          style={
            !isPngPublicationTemplate
              ? {
                  // Position row axis label: Use smaller of calculated height or viewport (100vh)
                  // Offset by heightOffset for multiple rows, but only if content height exceeds minimum
                  // ROW_HEIGHT_MIN ensures label doesn't position incorrectly for very short content
                  height: `calc(min(100vh, ${rowAxisHeight}px) - ${rowAxisHeight > ROW_HEIGHT_MIN.value ? heightOffset : 0}px)`,
                  ...(rowAxisHeight > heightOffset ? { top: `${heightOffset}px` } : {}),
                }
              : {}
          }
          onClick={(e) => {
            publishEvent('axisLabelClicked', { axis: 'row', anchorEl: e.currentTarget });
          }}
        >
          {rowAxisLabelText}
        </RowAxisLabel>
      </RowAxis>
    </RowAxisContainer>
  );
});

export function getColumnAxisLabelWidth(columnsCount: number, isCompact: boolean): number {
  const totalGap = CELL_GAP.value * (columnsCount - 1);
  return columnsCount * getCellWidth(isCompact) + totalGap;
}

export function getRowAxisLabelWidth(
  rowHeaderIDs: string[],
  getRowHeight: (rowID: string) => number,
): number {
  let rowAxisHeight = 0;
  if (!getRowHeight) {
    return rowAxisHeight;
  }
  rowHeaderIDs.forEach((headerID) => {
    rowAxisHeight = rowAxisHeight + getRowHeight(headerID);
  });

  rowAxisHeight = rowAxisHeight + CELL_GAP.value * (rowHeaderIDs.length - 1);
  return rowAxisHeight;
}
